import { useState } from 'react'
import type { PistaCena } from '../../dominio/didatico/pistasCena'
import { Rotulo3D } from '../componentes/Rotulo3D'

interface Props {
  pistas: readonly PistaCena[]
  pistaSelecionadaId: string | null
  aoSelecionarPista: (id: string | null) => void
  visivel?: boolean
}

/**
 * Renderiza marcadores tridimensionais (hotspots de evidência e trauma)
 * no interior e na estrutura do veículo para o "CSI do Resgate" e inspeção pericial.
 */
export function PistasCena3D({
  pistas,
  pistaSelecionadaId,
  aoSelecionarPista,
  visivel = true,
}: Props) {
  const [hoverId, setHoverId] = useState<string | null>(null)

  if (!visivel || pistas.length === 0) return null

  return (
    <group name="pistas-cena-3d">
      {pistas.map((pista) => {
        const selecionada = pista.id === pistaSelecionadaId
        const emHover = pista.id === hoverId
        const cor = selecionada ? '#f59e0b' : emHover ? '#38bdf8' : '#e11d48'
        const escala = selecionada ? 1.4 : emHover ? 1.2 : 1.0

        return (
          <group
            key={pista.id}
            position={[pista.posicao3D[0], pista.posicao3D[1], pista.posicao3D[2]]}
            onClick={(e) => {
              e.stopPropagation()
              aoSelecionarPista(selecionada ? null : pista.id)
            }}
            onPointerOver={(e) => {
              e.stopPropagation()
              setHoverId(pista.id)
              document.body.style.cursor = 'pointer'
            }}
            onPointerOut={() => {
              setHoverId(null)
              document.body.style.cursor = 'auto'
            }}
          >
            {/* Esfera central do hotspot */}
            <mesh scale={[escala, escala, escala]}>
              <sphereGeometry args={[0.045, 16, 16]} />
              <meshStandardMaterial
                color={cor}
                emissive={cor}
                emissiveIntensity={selecionada ? 0.9 : 0.4}
                roughness={0.2}
              />
            </mesh>

            {/* Anel pulsante indicador */}
            <mesh rotation={[-Math.PI / 2, 0, 0]}>
              <ringGeometry args={[0.065 * escala, 0.085 * escala, 24]} />
              <meshBasicMaterial
                color={cor}
                transparent
                opacity={selecionada ? 0.9 : 0.55}
                depthWrite={false}
              />
            </mesh>

            {/* Haste de fixação */}
            <mesh position={[0, -0.04, 0]}>
              <cylinderGeometry args={[0.006, 0.006, 0.08, 8]} />
              <meshBasicMaterial color={cor} opacity={0.7} transparent />
            </mesh>

            {/* Rótulo 3D descritivo */}
            {(selecionada || emHover) && (
              <Rotulo3D
                posicao={[0, 0.12, 0]}
                texto={pista.titulo}
                subtexto={pista.subtitulo}
                variante="achado"
                fator={6}
              />
            )}
          </group>
        )
      })}
    </group>
  )
}
